import { useState } from "react"
import { DateTime } from "luxon"
import CalendarComponent from "../components/CalendarComponent"
import { DayMenuDialog } from "../components/dayMenuDialog/DayMenuDialog"
import { MenuPlaningQuery, MenusListResponse, fetchMenusList } from "../menuPlanningApi"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useDateTime } from "../../../../localization/useDateTime"

export type MenuCalendarEvent = {
    title: string
    date: string
    id: string
    backgroundColor?: string
}

const selectMenus = (data: MenusListResponse): MenuCalendarEvent[] => {
    const menuPlanning = data?.menuPlanning
    if (!menuPlanning || !menuPlanning.weeks) {
        return []
    }
    const calendarEvents = menuPlanning.weeks
        .flatMap((week) => week.dailyMenus)
        .filter((menu) => menu.isActive)
        .flatMap((menu) => {
            const title = menu.dishes.map((dish) => dish.name).join(", ")
            return [
                {
                    title: title,
                    date: menu.publishDate,
                    ignoreAllDayLabel: true,
                    // allDay: true,
                    id: menu.id.toString(),
                    ...(menu?.color && { backgroundColor: menu?.color }),
                },
            ]
        })
    return calendarEvents
}

export function MenuCalendar() {
    const { merchantId } = useMerchant()
    const dateTimeFactory = useDateTime()

    const today = dateTimeFactory.now()

    const [dayMenuModalState, setDayMenuModalState] = useState<{
        show: boolean
        menuId: null | number
        dateInfo: DateTime
    }>({
        show: false,
        menuId: null,
        dateInfo: today,
    })

    const [startDate, setStartDate] = useState<DateTime>(today.startOf("month"))
    const [endDate, setEndDate] = useState<DateTime>(today.endOf("month"))

    const { data: events = [] } = useApiQuery(fetchMenusList, {
        queryName: MenuPlaningQuery.LIST,
        dependencies: {
            merchantId,
            from: startDate.toISODate(),
            to: endDate.toISODate(),
        },
        select: selectMenus,
    })
    const handleDateClick = (dateStr: string, allDay: boolean) => {
        const existingMenu = events.find((event) => event.date === dateStr)

        setDayMenuModalState({
            show: true,
            menuId: existingMenu ? +existingMenu.id : null,
            dateInfo: dateTimeFactory.fromISO(dateStr),
        })
    }

    const handleEventClick = (eventInfo: { id: number; date: string }) => {
        setDayMenuModalState({
            show: true,
            menuId: eventInfo.id,
            dateInfo: dateTimeFactory.fromISO(eventInfo.date),
        })
    }

    return (
        <div>
            <CalendarComponent
                events={events}
                onDateClick={handleDateClick}
                onEventClick={handleEventClick}
                setEndDate={setEndDate}
                setStartDate={setStartDate}
            />
            {dayMenuModalState.show && (
                <DayMenuDialog
                    open={dayMenuModalState.show}
                    menuId={dayMenuModalState.menuId}
                    dateInfo={dayMenuModalState.dateInfo}
                    onClose={() =>
                        setDayMenuModalState({
                            show: false,
                            menuId: null,
                            dateInfo: today,
                        })
                    }
                />
            )}
        </div>
    )
}
